import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  UserProgress, 
  WorkoutSession, 
  ExerciseRecord, 
  WorkoutDay, 
  Exercise,
  CurrentWorkoutState,
  CurrentSet,
  CurrentExerciseProgress,
  RPE
} from '../types/workout';
import { DatabaseService } from '../services/databaseService';
import { WorkoutUtils } from '../utils/workoutUtils';
import { useAuth } from '../contexts/AuthContext';
import exerciseData from '../exercises.json';

interface UseWorkoutReturn {
  // State
  userProgress: UserProgress | null;
  currentWorkoutState: CurrentWorkoutState | null;
  isLoading: boolean;
  error: string | null;
  isOnline: boolean;

  // Core actions
  initializeProgress: () => Promise<void>;
  resetProgress: () => Promise<void>;
  
  // Current workout actions
  initializeCurrentWorkout: (exercises: Exercise[]) => Promise<void>;
  startWorkout: () => Promise<void>;
  updateCurrentSet: (exerciseIndex: number, setIndex: number, weight: number, reps: number, actualRpe?: RPE) => Promise<void>;
  completeCurrentExercise: (exerciseIndex: number) => Promise<void>;
  isExerciseCompleted: (exerciseIndex: number) => boolean;
  finishCurrentWorkout: () => Promise<void>;
  resetCurrentWorkout: () => Promise<void>;
  
  // Progress actions
  progressToNextDay: () => Promise<void>;
  skipCurrentDay: () => Promise<void>;
  advanceToNextWeek: () => Promise<void>;
  
  // Debug actions
  addRandomWeek1And2Stats: () => Promise<void>;
  
  // Data getters
  getCurrentWeekExercises: () => Exercise[] | string;
  getWorkoutStats: () => any;
  getRecentWorkouts: (limit?: number) => WorkoutSession[];
  getElapsedTime: () => number; // Returns elapsed time in minutes
  isWorkoutStarted: () => boolean;
  canCompleteWorkout: () => boolean;

  // Cache status utility
  getCacheStatus: () => any;

  // Debug function to verify workout data integrity
  debugWorkoutData: () => any;

  // Cleanup function to remove duplicate workout sessions
  cleanupDuplicateSessions: () => Promise<void>;
}

export const useWorkout = (): UseWorkoutReturn => {
  const { user } = useAuth();
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [currentWorkoutState, setCurrentWorkoutState] = useState<CurrentWorkoutState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [recentWorkouts, setRecentWorkouts] = useState<WorkoutSession[]>([]);
  
  // Add refs to track data freshness and prevent unnecessary reloads
  const lastLoadedRef = useRef<number>(0);
  const userIdRef = useRef<string | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  
  // Cache duration: 5 minutes (300000ms) - only refresh if data is older than this
  const CACHE_DURATION = 5 * 60 * 1000;
  
  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Add Page Visibility API to handle app focus/blur intelligently
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isInitializedRef.current) {
        // App became visible again - check if we need to refresh data
        const now = Date.now();
        const timeSinceLastLoad = now - lastLoadedRef.current;
        
        // Only refresh if data is older than cache duration
        if (timeSinceLastLoad > CACHE_DURATION) {
          console.log('🔄 App became visible and data is stale, refreshing...');
          if (user && userIdRef.current === user.id) {
            loadUserData(false); // false = don't show loading spinner for background refresh
          }
        } else {
          console.log('✅ App became visible but data is still fresh, skipping refresh');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // Helper function to update user progress cache
  const updateUserProgressCache = useCallback((progress: UserProgress) => {
    if (!user) return;
    try {
      localStorage.setItem(`user_progress_${user.id}`, JSON.stringify(progress));
      localStorage.setItem(`user_progress_timestamp_${user.id}`, Date.now().toString());
    } catch (err) {
      console.warn('Failed to update user progress cache:', err);
    }
  }, [user]);

  const initializeNewUser = useCallback(async (): Promise<UserProgress> => {
    if (!user) throw new Error('No authenticated user');
    
    const progress: UserProgress = {
      userId: user.id,
      currentWeek: 1,
      currentDay: WorkoutDay.MONDAY,
      workoutHistory: [],
      personalRecords: {}
    };
    
    await DatabaseService.saveUserProgress(progress);
    updateUserProgressCache(progress); // Cache the new progress
    return progress;
  }, [user, updateUserProgressCache]);

  const loadUserData = useCallback(async (showLoading: boolean = true) => {
    if (!user) return;
    
    try {
      if (showLoading) {
        setIsLoading(true);
      }
      setError(null);
      
      // Update last loaded timestamp
      lastLoadedRef.current = Date.now();
      isInitializedRef.current = true;
      
      // Try to load user progress from localStorage first for faster initial load
      let progress: UserProgress | null = null;
      let useLocalCache = false;
      
      try {
        const cachedProgress = localStorage.getItem(`user_progress_${user.id}`);
        const cacheTimestamp = localStorage.getItem(`user_progress_timestamp_${user.id}`);
        
        if (cachedProgress && cacheTimestamp) {
          const cacheAge = Date.now() - parseInt(cacheTimestamp);
          if (cacheAge < CACHE_DURATION) {
            progress = JSON.parse(cachedProgress);
            useLocalCache = true;
            console.log('✅ Using cached user progress (age:', Math.round(cacheAge / 1000), 'seconds)');
          }
        }
      } catch (err) {
        console.warn('Failed to load user progress from cache:', err);
      }
      
      // If no valid cache, load from database
      if (!progress) {
        console.log('🔄 Loading user progress from database...');
        progress = await DatabaseService.getUserProgress();
        
        if (!progress) {
          // Initialize new user
          progress = await initializeNewUser();
        }
        
        // Cache the progress data
        try {
          localStorage.setItem(`user_progress_${user.id}`, JSON.stringify(progress));
          localStorage.setItem(`user_progress_timestamp_${user.id}`, Date.now().toString());
        } catch (err) {
          console.warn('Failed to cache user progress:', err);
        }
      }
      
      setUserProgress(progress);
      
      // Load current workout state - check localStorage first
      let currentState: CurrentWorkoutState | null = null;
      
      try {
        const localState = localStorage.getItem('current_workout_state');
        if (localState) {
          currentState = JSON.parse(localState);
          console.log('✅ Loaded current workout from localStorage');
        }
      } catch (err) {
        console.warn('Failed to load from localStorage:', err);
      }
      
      // If no localStorage state, try database
      if (!currentState) {
        currentState = await DatabaseService.getCurrentWorkoutState();
        if (currentState) {
          console.log('✅ Loaded current workout from database');
          // Save to localStorage for future use
          localStorage.setItem('current_workout_state', JSON.stringify(currentState));
        }
      }
      
      setCurrentWorkoutState(currentState);
      
      // Load recent workouts - also try cache first if we used cached progress
      let recent: WorkoutSession[] = [];
      
      if (useLocalCache && progress?.workoutHistory) {
        // Use workout history from cached progress for recent workouts
        recent = progress.workoutHistory.slice(0, 10);
        console.log('✅ Using recent workouts from cached progress');
      } else {
        // Load from database
        recent = await DatabaseService.getRecentWorkouts(10);
      }
      
      setRecentWorkouts(recent);
      
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load workout data');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [user, updateUserProgressCache, initializeNewUser]);

  // Initialize when user is available - but only if user actually changed
  useEffect(() => {
    if (user && user.id !== userIdRef.current) {
      console.log('🔄 User changed, loading data...');
      userIdRef.current = user.id;
      loadUserData();
    } else if (user && !isInitializedRef.current) {
      console.log('🔄 Initial load for existing user...');
      loadUserData();
    } else if (!user && userIdRef.current) {
      // User signed out - clear cache and reset state
      console.log('🧹 User signed out, clearing cache...');
      const oldUserId = userIdRef.current;
      try {
        localStorage.removeItem(`user_progress_${oldUserId}`);
        localStorage.removeItem(`user_progress_timestamp_${oldUserId}`);
        localStorage.removeItem('current_workout_state');
      } catch (err) {
        console.warn('Failed to clear cache on sign out:', err);
      }
      
      userIdRef.current = null;
      isInitializedRef.current = false;
      lastLoadedRef.current = 0;
      setUserProgress(null);
      setCurrentWorkoutState(null);
      setRecentWorkouts([]);
      setIsLoading(false);
      setError(null);
    }
  }, [user, loadUserData]);

  const initializeProgress = useCallback(async () => {
    if (!user) return;
    
    try {
      setError(null);
      const progress = await initializeNewUser();
      setUserProgress(progress);
      setCurrentWorkoutState(null);
      await DatabaseService.clearCurrentWorkoutState();
      localStorage.removeItem('current_workout_state');
    } catch (err) {
      console.error('Error initializing progress:', err);
      setError('Failed to initialize progress');
    }
  }, [user, updateUserProgressCache]);

  const resetProgress = useCallback(async () => {
    if (!user) return;
    
    try {
      setError(null);
      await DatabaseService.resetUserData();
      
      // Clear cache
      localStorage.removeItem(`user_progress_${user.id}`);
      localStorage.removeItem(`user_progress_timestamp_${user.id}`);
      
      const progress = await initializeNewUser();
      setUserProgress(progress);
      setCurrentWorkoutState(null);
      localStorage.removeItem('current_workout_state');
      setRecentWorkouts([]);
    } catch (err) {
      console.error('Error resetting progress:', err);
      setError('Failed to reset progress');
    }
  }, [user, updateUserProgressCache]);

  const getCurrentWeekExercises = useCallback((): Exercise[] | string => {
    if (!userProgress) return [];
    
    const program = exerciseData as any;
    const weekData = program[userProgress.currentWeek.toString()];
    
    if (!weekData) {
      console.warn(`Week ${userProgress.currentWeek} not found`);
      return [];
    }
    
    const dayWorkout = weekData[userProgress.currentDay];
    return dayWorkout || [];
  }, [userProgress]);

  // Helper function to get exercises for any week/day combination
  const getExercisesForWeekAndDay = useCallback((week: number, day: WorkoutDay): Exercise[] => {
    const program = exerciseData as any;
    const weekData = program[week.toString()];
    
    if (!weekData) {
      console.warn(`Week ${week} not found`);
      return [];
    }
    
    const dayWorkout = weekData[day];
    return Array.isArray(dayWorkout) ? dayWorkout : [];
  }, []);

  const initializeCurrentWorkout = useCallback(async (exercises: Exercise[]) => {
    if (!userProgress) return;
    
    try {
      const workoutState: CurrentWorkoutState = {
        week: userProgress.currentWeek,
        day: userProgress.currentDay,
        exercises: exercises.map((exercise, index) => ({
          exerciseIndex: index,
          exerciseName: exercise.exercise,
          sets: generateSetsForExercise(exercise),
          completed: false
        })),
        startTime: new Date().toISOString()
      };
      
      // Save to localStorage initially to avoid database conflicts
      localStorage.setItem('current_workout_state', JSON.stringify(workoutState));
      setCurrentWorkoutState(workoutState);
      
      console.log('✅ Workout initialized in localStorage');
    } catch (err) {
      console.error('Error initializing current workout:', err);
      setError('Failed to initialize workout');
    }
  }, [userProgress]);

  const generateSetsForExercise = (exercise: Exercise): CurrentSet[] => {
    const sets: CurrentSet[] = [];
    
    // Parse warmup sets
    const warmupRange = WorkoutUtils.parseSetRange(exercise.warmup_sets);
    for (let i = 0; i < warmupRange.max; i++) {
      sets.push({
        setNumber: i + 1, // Warmup sets numbered 1, 2, 3...
        type: 'warmup',
        weight: null,
        reps: null,
        completed: false
      });
    }
    
    // Parse working sets
    const workingRange = WorkoutUtils.parseSetRange(exercise.working_sets);
    for (let i = 0; i < workingRange.max; i++) {
      sets.push({
        setNumber: i + 1, // Working sets numbered 1, 2, 3... (independent of warmup)
        type: 'working',
        weight: null,
        reps: null,
        completed: false
      });
    }
    
    return sets;
  };

  // Generate skipped sets for an exercise (for when a day is skipped)
  const generateSkippedSetsForExercise = (exercise: Exercise) => {
    const sets = [];
    
    // Parse warmup sets
    const warmupRange = WorkoutUtils.parseSetRange(exercise.warmup_sets);
    for (let i = 0; i < warmupRange.max; i++) {
      sets.push({
        setNumber: i + 1, // Warmup sets numbered 1, 2, 3...
        setType: 'warmup' as const,
        weight: 0,
        reps: 0,
        completed: false
      });
    }
    
    // Parse working sets
    const workingRange = WorkoutUtils.parseSetRange(exercise.working_sets);
    for (let i = 0; i < workingRange.max; i++) {
      sets.push({
        setNumber: i + 1, // Working sets numbered 1, 2, 3... (independent of warmup)
        setType: 'working' as const,
        weight: 0,
        reps: 0,
        completed: false
      });
    }
    
    return sets;
  };

  const updateCurrentSet = useCallback(async (exerciseIndex: number, setIndex: number, weight: number, reps: number, actualRpe?: RPE) => {
    if (!currentWorkoutState) return;
    
    try {
      const updatedState = { ...currentWorkoutState };
      const exercise = updatedState.exercises[exerciseIndex];
      
      if (exercise && exercise.sets[setIndex]) {
        exercise.sets[setIndex] = {
          ...exercise.sets[setIndex],
          weight,
          reps,
          actualRpe,
          completed: true
        };
        
        // Save to localStorage instead of database for better performance
        localStorage.setItem('current_workout_state', JSON.stringify(updatedState));
        setCurrentWorkoutState(updatedState);
        
        console.log('✅ Set updated in localStorage:', { exerciseIndex, setIndex, weight, reps, actualRpe });
      }
    } catch (err) {
      console.error('Error updating set:', err);
      setError('Failed to update set');
    }
  }, [currentWorkoutState]);

  const completeCurrentExercise = useCallback(async (exerciseIndex: number) => {
    if (!userProgress) return;
    
    try {
      // Always get the latest workout state from localStorage to ensure we have the most recent set data
      let latestWorkoutState: CurrentWorkoutState | null = null;
      
      try {
        const localState = localStorage.getItem('current_workout_state');
        if (localState) {
          latestWorkoutState = JSON.parse(localState);
          console.log('✅ Using latest workout state from localStorage for exercise completion');
        }
      } catch (err) {
        console.warn('Failed to load latest workout state from localStorage:', err);
        latestWorkoutState = currentWorkoutState;
      }
      
      if (!latestWorkoutState) {
        console.error('No workout state available for exercise completion');
        setError('No active workout found');
        return;
      }
      
      const updatedState = { ...latestWorkoutState };
      const exercise = updatedState.exercises[exerciseIndex];
      
      if (exercise) {
        exercise.completed = true;
        
        // Save to both localStorage and database when exercise is completed
        localStorage.setItem('current_workout_state', JSON.stringify(updatedState));
        await DatabaseService.saveCurrentWorkoutState(updatedState);
        setCurrentWorkoutState(updatedState);
        
        // Check for existing session in DATABASE, not just memory
        const todayDate = new Date().toISOString().split('T')[0];
        let existingSession: WorkoutSession | null = null;
        
        try {
          // Get all sessions for today from database to check for duplicates
          const allSessions = await DatabaseService.getRecentWorkouts(50); // Get more sessions to ensure we find today's
          existingSession = allSessions.find(session => 
            session.week === latestWorkoutState!.week && 
            session.day === latestWorkoutState!.day &&
            session.date === todayDate
          ) || null;
          
          if (existingSession) {
            console.log('🔍 Found existing session in database:', existingSession.id);
          } else {
            console.log('🆕 No existing session found, will create new one');
          }
        } catch (err) {
          console.warn('Failed to check for existing sessions, will create new one:', err);
        }
        
        // Create workout session from current state - ensure we capture ALL set data
        const session: WorkoutSession = {
          id: existingSession?.id || WorkoutUtils.generateWorkoutId(),
          date: todayDate,
          week: latestWorkoutState.week,
          day: latestWorkoutState.day,
          exercises: updatedState.exercises.map(ex => {
            // Include ALL sets, not just completed ones, but mark completion status
            const allSets = ex.sets.map(set => ({
              setNumber: set.setNumber,
              setType: set.type,
              weight: set.weight || 0,
              reps: set.reps || 0,
              actualRpe: set.actualRpe,
              completed: set.completed
            }));
            
            // Calculate total volume from completed sets only
            const totalVolume = ex.sets
              .filter(set => set.completed && set.weight && set.reps)
              .reduce((total, set) => total + (set.weight || 0) * (set.reps || 0), 0);
            
            return {
              exerciseName: ex.exerciseName,
              date: todayDate,
              sets: allSets,
              totalVolume
            };
          }),
          completed: updatedState.exercises.every(ex => ex.completed), // Mark as completed only if ALL exercises are done
          duration: Math.round((Date.now() - new Date(latestWorkoutState.startTime).getTime()) / 60000)
        };
        
        console.log('📊 Creating/updating workout session with set data:', {
          exerciseIndex,
          exerciseName: exercise.exerciseName,
          sessionId: session.id,
          isUpdate: !!existingSession,
          setsCount: exercise.sets.length,
          completedSets: exercise.sets.filter(s => s.completed).length,
          sessionExercise: session.exercises.find(e => e.exerciseName === exercise.exerciseName)
        });
        
        // Save workout session to database
        await DatabaseService.saveWorkoutSession(session);
        
        // Update in-memory workout history
        const updatedProgress = { ...userProgress };
        const existingSessionIndex = updatedProgress.workoutHistory.findIndex(
          s => s.id === session.id
        );
        
        if (existingSessionIndex >= 0) {
          // Update existing session in memory
          updatedProgress.workoutHistory[existingSessionIndex] = session;
          console.log('🔄 Updated existing session in memory');
        } else {
          // Add new session to memory
          updatedProgress.workoutHistory.push(session);
          console.log('🆕 Added new session to memory');
        }
        
        // Update personal records if this is a new PR
        updatePersonalRecords(updatedProgress, session);
        
        // Save updated progress
        await DatabaseService.saveUserProgress(updatedProgress);
        updateUserProgressCache(updatedProgress); // Update cache
        setUserProgress(updatedProgress);
        
        // Reload recent workouts to reflect changes
        const recent = await DatabaseService.getRecentWorkouts(10);
        setRecentWorkouts(recent);
        
        console.log('✅ Exercise completed and workout session updated:', exerciseIndex);
      }
    } catch (err) {
      console.error('Error completing exercise:', err);
      setError('Failed to complete exercise');
    }
  }, [currentWorkoutState, userProgress, updateUserProgressCache]);

  const isExerciseCompleted = useCallback((exerciseIndex: number): boolean => {
    if (!currentWorkoutState) return false;
    const exercise = currentWorkoutState.exercises[exerciseIndex];
    return exercise?.completed || false;
  }, [currentWorkoutState]);

  const finishCurrentWorkout = useCallback(async () => {
    if (!currentWorkoutState || !userProgress) return;
    
    try {
      // Find the existing session for today (should already exist from completeCurrentExercise)
      const updatedProgress = { ...userProgress };
      const existingSessionIndex = updatedProgress.workoutHistory.findIndex(
        session => session.week === currentWorkoutState.week && 
                  session.day === currentWorkoutState.day &&
                  session.date === new Date().toISOString().split('T')[0]
      );
      
      if (existingSessionIndex >= 0) {
        // Mark the existing session as completed
        updatedProgress.workoutHistory[existingSessionIndex].completed = true;
        await DatabaseService.saveWorkoutSession(updatedProgress.workoutHistory[existingSessionIndex]);
      }
      
      // Progress to next day
      updatedProgress.currentDay = WorkoutUtils.getNextDay(updatedProgress.currentDay) as WorkoutDay;
      
      // Check if we should advance to next week
      if (WorkoutUtils.shouldAdvanceWeek(updatedProgress.currentDay)) {
        updatedProgress.currentWeek = Math.min(12, updatedProgress.currentWeek + 1);
        updatedProgress.currentDay = WorkoutDay.MONDAY;
      }
      
      await DatabaseService.saveUserProgress(updatedProgress);
      updateUserProgressCache(updatedProgress); // Update cache
      setUserProgress(updatedProgress);
      
      // Clear current workout
      await DatabaseService.clearCurrentWorkoutState();
      localStorage.removeItem('current_workout_state');
      setCurrentWorkoutState(null);
      
      // Reload recent workouts
      const recent = await DatabaseService.getRecentWorkouts(10);
      setRecentWorkouts(recent);
      
    } catch (err) {
      console.error('Error finishing workout:', err);
      setError('Failed to finish workout');
    }
  }, [currentWorkoutState, userProgress, updateUserProgressCache]);

  const updatePersonalRecords = (progress: UserProgress, session: WorkoutSession) => {
    session.exercises.forEach(exercise => {
      const currentRecord = progress.personalRecords[exercise.exerciseName];
      
      exercise.sets.forEach(set => {
        if (!currentRecord || set.weight > (currentRecord.sets[0]?.weight || 0)) {
          progress.personalRecords[exercise.exerciseName] = {
            ...exercise,
            personalRecord: true
          };
        }
      });
    });
  };

  const resetCurrentWorkout = useCallback(async () => {
    try {
      await DatabaseService.clearCurrentWorkoutState();
      localStorage.removeItem('current_workout_state');
      setCurrentWorkoutState(null);
    } catch (err) {
      console.error('Error resetting current workout:', err);
      setError('Failed to reset workout');
    }
  }, []);

  const progressToNextDay = useCallback(async () => {
    if (!userProgress) return;
    
    try {
      const updatedProgress = { ...userProgress };
      updatedProgress.currentDay = WorkoutUtils.getNextDay(updatedProgress.currentDay) as WorkoutDay;
      
      if (WorkoutUtils.shouldAdvanceWeek(updatedProgress.currentDay)) {
        updatedProgress.currentWeek = Math.min(12, updatedProgress.currentWeek + 1);
        updatedProgress.currentDay = WorkoutDay.MONDAY;
      }
      
      await DatabaseService.saveUserProgress(updatedProgress);
      updateUserProgressCache(updatedProgress); // Update cache
      setUserProgress(updatedProgress);
    } catch (err) {
      console.error('Error progressing to next day:', err);
      setError('Failed to progress to next day');
    }
  }, [userProgress, updateUserProgressCache]);

  const skipCurrentDay = useCallback(async () => {
    if (!userProgress) return;
    
    try {
      // Get the expected exercises for this day
      const expectedExercises = getExercisesForWeekAndDay(userProgress.currentWeek, userProgress.currentDay);
      
      // Create workout session with expected exercises but all sets skipped
      const session: WorkoutSession = {
        id: WorkoutUtils.generateWorkoutId(),
        date: new Date().toISOString().split('T')[0],
        week: userProgress.currentWeek,
        day: userProgress.currentDay,
        exercises: expectedExercises.map(exercise => ({
          exerciseName: exercise.exercise,
          date: new Date().toISOString().split('T')[0],
          sets: generateSkippedSetsForExercise(exercise),
          totalVolume: 0
        })),
        completed: false,
        notes: 'skipped'
      };
      
      await DatabaseService.saveWorkoutSession(session);
      
      // Progress to next day
      await progressToNextDay();
      
      // Clear current workout if any
      await DatabaseService.clearCurrentWorkoutState();
      localStorage.removeItem('current_workout_state');
      setCurrentWorkoutState(null);
      
    } catch (err) {
      console.error('Error skipping day:', err);
      setError('Failed to skip day');
    }
  }, [userProgress, progressToNextDay, getExercisesForWeekAndDay]);

  const advanceToNextWeek = useCallback(async () => {
    if (!userProgress) return;
    
    try {
      const updatedProgress = { ...userProgress };
      updatedProgress.currentWeek = Math.min(12, updatedProgress.currentWeek + 1);
      updatedProgress.currentDay = WorkoutDay.MONDAY;
      
      await DatabaseService.saveUserProgress(updatedProgress);
      updateUserProgressCache(updatedProgress); // Update cache
      setUserProgress(updatedProgress);
      
      // Clear current workout
      await DatabaseService.clearCurrentWorkoutState();
      localStorage.removeItem('current_workout_state');
      setCurrentWorkoutState(null);
      
    } catch (err) {
      console.error('Error advancing to next week:', err);
      setError('Failed to advance to next week');
    }
  }, [userProgress, updateUserProgressCache]);

  const addRandomWeek1And2Stats = useCallback(async () => {
    if (!userProgress) return;
    
    try {
      const updatedProgress = { ...userProgress };
      
      // Clear any existing workout history to avoid conflicts
      updatedProgress.workoutHistory = [];
      
      // Generate random workout sessions for weeks 1 and 2
      const workoutDays = [WorkoutDay.MONDAY, WorkoutDay.TUESDAY, WorkoutDay.THURSDAY, WorkoutDay.FRIDAY, WorkoutDay.SATURDAY];
      
      for (let week = 1; week <= 2; week++) {
        for (const day of workoutDays) {
          // Get exercises for the specific week and day
          const program = exerciseData as any;
          const weekData = program[week.toString()];
          const exercises = weekData ? weekData[day] : [];
          
          if (Array.isArray(exercises) && exercises.length > 0) {
            const session: WorkoutSession = {
              id: WorkoutUtils.generateWorkoutId(),
              date: new Date(Date.now() - (14 - week) * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              week,
              day,
              exercises: exercises.map(exercise => ({
                exerciseName: exercise.exercise,
                date: new Date().toISOString().split('T')[0],
                sets: (() => {
                  // Use the actual exercise configuration to generate correct sets
                  const warmupRange = WorkoutUtils.parseSetRange(exercise.warmup_sets);
                  const workingRange = WorkoutUtils.parseSetRange(exercise.working_sets);
                  const sets = [];
                  
                  // Generate warmup sets
                  for (let i = 0; i < warmupRange.max; i++) {
                    const warmupRpeValues = [RPE.RPE_6, RPE.RPE_7, RPE.RPE_8, RPE.RPE_9];
                    sets.push({
                      setNumber: i + 1,
                      setType: 'warmup' as const,
                      weight: Math.floor(Math.random() * 100) + 50,
                      reps: Math.floor(Math.random() * 5) + 8,
                      actualRpe: warmupRpeValues[Math.floor(Math.random() * warmupRpeValues.length)],
                      completed: true
                    });
                  }
                  
                  // Generate working sets
                  for (let i = 0; i < workingRange.max; i++) {
                    const workingRpeValues = [RPE.RPE_7, RPE.RPE_8, RPE.RPE_9, RPE.RPE_10];
                    sets.push({
                      setNumber: i + 1,
                      setType: 'working' as const,
                      weight: Math.floor(Math.random() * 100) + 50,
                      reps: Math.floor(Math.random() * 5) + 8,
                      actualRpe: workingRpeValues[Math.floor(Math.random() * workingRpeValues.length)],
                      completed: true
                    });
                  }
                  
                  return sets;
                })(),
                totalVolume: 0
              })),
              completed: true // Mark as fully completed for demo data
            };
            
            // Calculate total volume
            session.exercises.forEach(ex => {
              ex.totalVolume = ex.sets.reduce((total, set) => total + set.weight * set.reps, 0);
            });
            
            // Add realistic workout duration (45-90 minutes)
            session.duration = Math.floor(Math.random() * 45) + 45;
            
            await DatabaseService.saveWorkoutSession(session);
            updatedProgress.workoutHistory.push(session);
          }
        }
      }
      
      // Advance user to week 3, Monday to show progress
      updatedProgress.currentWeek = 3;
      updatedProgress.currentDay = WorkoutDay.MONDAY;
      
      await DatabaseService.saveUserProgress(updatedProgress);
      setUserProgress(updatedProgress);
      
      // Get exercises for week 3, Monday
      const program = exerciseData as any;
      const week3Data = program['3'];
      const mondayExercises = week3Data ? week3Data[WorkoutDay.MONDAY] : [];
      
      if (Array.isArray(mondayExercises) && mondayExercises.length > 0) {
        // Initialize current workout for week 3, Monday with some exercises completed
        const currentWorkout: CurrentWorkoutState = {
          week: 3,
          day: WorkoutDay.MONDAY,
          exercises: mondayExercises.map((exercise, index) => {
            const sets = generateSetsForExercise(exercise);
            
            // Mark first 2 exercises as completed for demonstration
            if (index < 2) {
              const workingRpeValues = [RPE.RPE_7, RPE.RPE_8, RPE.RPE_9, RPE.RPE_10];
              sets.forEach((set, setIndex) => {
                set.weight = Math.floor(Math.random() * 100) + 60;
                set.reps = Math.floor(Math.random() * 5) + 8;
                set.completed = true;
                // Add RPE for working sets only
                if (set.type === 'working') {
                  set.actualRpe = workingRpeValues[Math.floor(Math.random() * workingRpeValues.length)];
                }
              });
            }
            
            return {
              exerciseIndex: index,
              exerciseName: exercise.exercise,
              sets,
              completed: index < 2 // Mark first 2 exercises as completed
            };
          }),
          startTime: new Date().toISOString()
        };
        
        // Save current workout state to both database and localStorage
        await DatabaseService.saveCurrentWorkoutState(currentWorkout);
        localStorage.setItem('current_workout_state', JSON.stringify(currentWorkout));
        setCurrentWorkoutState(currentWorkout);
        
        // Create a partial workout session for week 3, Monday (in progress)
        const partialSession: WorkoutSession = {
          id: WorkoutUtils.generateWorkoutId(),
          date: new Date().toISOString().split('T')[0],
          week: 3,
          day: WorkoutDay.MONDAY,
          exercises: currentWorkout.exercises.slice(0, 2).map(ex => ({
            exerciseName: ex.exerciseName,
            date: new Date().toISOString().split('T')[0],
            sets: ex.sets.filter(set => set.completed).map(set => ({
              setNumber: set.setNumber,
              setType: set.type,
              weight: set.weight || 0,
              reps: set.reps || 0,
              actualRpe: set.actualRpe,
              completed: true
            })),
            totalVolume: ex.sets
              .filter(set => set.completed)
              .reduce((total, set) => total + (set.weight || 0) * (set.reps || 0), 0)
          })),
          completed: false, // Mark as in progress since not all exercises are done
          duration: 15 // Sample duration
        };
        
        await DatabaseService.saveWorkoutSession(partialSession);
        updatedProgress.workoutHistory.push(partialSession);
        await DatabaseService.saveUserProgress(updatedProgress);
        updateUserProgressCache(updatedProgress); // Update cache
        setUserProgress(updatedProgress);
      }
      
      // Reload recent workouts
      const recent = await DatabaseService.getRecentWorkouts(10);
      setRecentWorkouts(recent);
      
    } catch (err) {
      console.error('Error adding random stats:', err);
      setError('Failed to add random stats');
    }
  }, [userProgress, generateSetsForExercise, updateUserProgressCache]);

  const getWorkoutStats = useCallback(() => {
    if (!userProgress) {
      return {
        currentWeek: 1,
        currentDay: 'Monday',
        totalWorkouts: 0,
        totalVolume: 0,
        averageRPE: 0,
        consistency: 0
      };
    }
    
    return {
      currentWeek: userProgress.currentWeek,
      currentDay: userProgress.currentDay,
      totalWorkouts: userProgress.workoutHistory.length,
      totalVolume: WorkoutUtils.calculateTotalVolume(userProgress.workoutHistory),
      averageRPE: WorkoutUtils.calculateAverageRPE(userProgress.workoutHistory),
      consistency: WorkoutUtils.calculateConsistency(userProgress, 4)
    };
  }, [userProgress]);

  const getRecentWorkouts = useCallback((limit: number = 10): WorkoutSession[] => {
    return recentWorkouts.slice(0, limit);
  }, [recentWorkouts]);

  // Utility function to check cache status (for debugging)
  const getCacheStatus = useCallback(() => {
    if (!user) return null;
    
    try {
      const cachedProgress = localStorage.getItem(`user_progress_${user.id}`);
      const cacheTimestamp = localStorage.getItem(`user_progress_timestamp_${user.id}`);
      const currentWorkoutCache = localStorage.getItem('current_workout_state');
      
      if (cachedProgress && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        return {
          hasProgressCache: true,
          cacheAge: Math.round(cacheAge / 1000), // in seconds
          isStale: cacheAge > CACHE_DURATION,
          hasWorkoutCache: !!currentWorkoutCache,
          lastLoaded: lastLoadedRef.current,
          timeSinceLastLoad: Math.round((Date.now() - lastLoadedRef.current) / 1000)
        };
      }
      
      return {
        hasProgressCache: false,
        cacheAge: 0,
        isStale: true,
        hasWorkoutCache: !!currentWorkoutCache,
        lastLoaded: lastLoadedRef.current,
        timeSinceLastLoad: Math.round((Date.now() - lastLoadedRef.current) / 1000)
      };
    } catch (err) {
      console.warn('Failed to check cache status:', err);
      return null;
    }
  }, [user]);

  // Debug function to verify workout data integrity
  const debugWorkoutData = useCallback(() => {
    if (!currentWorkoutState) {
      console.log('❌ No current workout state');
      return null;
    }
    
    try {
      const localState = localStorage.getItem('current_workout_state');
      const parsedLocalState = localState ? JSON.parse(localState) : null;
      
      const memoryState = currentWorkoutState;
      
      console.log('🔍 Workout Data Debug:', {
        hasLocalStorage: !!localState,
        hasMemoryState: !!memoryState,
        statesMatch: JSON.stringify(parsedLocalState) === JSON.stringify(memoryState),
        localStorageExercises: parsedLocalState?.exercises?.length || 0,
        memoryExercises: memoryState?.exercises?.length || 0,
        localStorageCompletedSets: parsedLocalState?.exercises?.reduce((total: number, ex: any) => 
          total + ex.sets.filter((s: any) => s.completed).length, 0) || 0,
        memoryCompletedSets: memoryState?.exercises?.reduce((total, ex) => 
          total + ex.sets.filter(s => s.completed).length, 0) || 0
      });
      
      // Check each exercise for data consistency
      if (parsedLocalState && memoryState) {
        parsedLocalState.exercises.forEach((localEx: any, index: number) => {
          const memoryEx = memoryState.exercises[index];
          if (memoryEx) {
            const localCompletedSets = localEx.sets.filter((s: any) => s.completed).length;
            const memoryCompletedSets = memoryEx.sets.filter(s => s.completed).length;
            
            if (localCompletedSets !== memoryCompletedSets) {
              console.warn(`⚠️ Set count mismatch for ${localEx.exerciseName}:`, {
                localStorage: localCompletedSets,
                memory: memoryCompletedSets
              });
            }
          }
        });
      }
      
      return {
        localStorage: parsedLocalState,
        memory: memoryState,
        consistent: JSON.stringify(parsedLocalState) === JSON.stringify(memoryState)
      };
    } catch (err) {
      console.error('Failed to debug workout data:', err);
      return null;
    }
  }, [currentWorkoutState]);

  // Cleanup function to remove duplicate workout sessions
  const cleanupDuplicateSessions = useCallback(async () => {
    if (!userProgress) return;
    
    try {
      console.log('🧹 Starting duplicate session cleanup...');
      
      // Get all sessions from database
      const allSessions = await DatabaseService.getRecentWorkouts(100);
      
      // Group sessions by week, day, and date
      const sessionGroups: { [key: string]: WorkoutSession[] } = {};
      
      allSessions.forEach(session => {
        const key = `${session.week}-${session.day}-${session.date}`;
        if (!sessionGroups[key]) {
          sessionGroups[key] = [];
        }
        sessionGroups[key].push(session);
      });
      
      // Find groups with duplicates
      const duplicateGroups = Object.entries(sessionGroups).filter(([_, sessions]) => sessions.length > 1);
      
      if (duplicateGroups.length === 0) {
        console.log('✅ No duplicate sessions found');
        return;
      }
      
      console.log(`🔍 Found ${duplicateGroups.length} groups with duplicate sessions`);
      
      // For each group with duplicates, keep the most complete session
      for (const [key, sessions] of duplicateGroups) {
        console.log(`🔄 Processing duplicates for ${key}:`, sessions.length, 'sessions');
        
        // Sort sessions by completeness (more exercises and completed sets = better)
        const sortedSessions = sessions.sort((a, b) => {
          const aExerciseCount = a.exercises.length;
          const bExerciseCount = b.exercises.length;
          const aCompletedSets = a.exercises.reduce((total, ex) => 
            total + ex.sets.filter(set => set.completed).length, 0);
          const bCompletedSets = b.exercises.reduce((total, ex) => 
            total + ex.sets.filter(set => set.completed).length, 0);
          
          // First sort by exercise count, then by completed sets
          if (bExerciseCount !== aExerciseCount) {
            return bExerciseCount - aExerciseCount;
          }
          return bCompletedSets - aCompletedSets;
        });
        
        const keepSession = sortedSessions[0];
        const deleteSessions = sortedSessions.slice(1);
        
        console.log(`✅ Keeping session ${keepSession.id} (${keepSession.exercises.length} exercises)`);
        
        // Delete the duplicate sessions
        for (const sessionToDelete of deleteSessions) {
          try {
            await DatabaseService.deleteWorkoutSession(sessionToDelete.id);
            console.log(`🗑️ Deleted duplicate session ${sessionToDelete.id}`);
          } catch (err) {
            console.error(`Failed to delete session ${sessionToDelete.id}:`, err);
          }
        }
      }
      
      // Reload user data to reflect changes
      await loadUserData(false);
      
      console.log('✅ Duplicate session cleanup completed');
      
    } catch (err) {
      console.error('Error during duplicate session cleanup:', err);
      setError('Failed to cleanup duplicate sessions');
    }
  }, [userProgress, loadUserData]);

  const startWorkout = useCallback(async () => {
    if (!userProgress) return;
    
    try {
      // Get current week exercises
      const currentExercises = getCurrentWeekExercises();
      
      if (!Array.isArray(currentExercises) || currentExercises.length === 0) {
        setError('No exercises available for today');
        return;
      }
      
      const workoutState: CurrentWorkoutState = {
        week: userProgress.currentWeek,
        day: userProgress.currentDay,
        exercises: currentExercises.map((exercise, index) => ({
          exerciseIndex: index,
          exerciseName: exercise.exercise,
          sets: generateSetsForExercise(exercise),
          completed: false
        })),
        startTime: new Date().toISOString()
      };
      
      // Save to localStorage and set state
      localStorage.setItem('current_workout_state', JSON.stringify(workoutState));
      setCurrentWorkoutState(workoutState);
      
      console.log('✅ Workout started with timer');
    } catch (err) {
      console.error('Error starting workout:', err);
      setError('Failed to start workout');
    }
  }, [userProgress, getCurrentWeekExercises, generateSetsForExercise]);

  const getElapsedTime = useCallback(() => {
    if (!currentWorkoutState || !currentWorkoutState.startTime) return 0;
    
    const startTime = new Date(currentWorkoutState.startTime).getTime();
    const currentTime = Date.now();
    const elapsedTime = Math.round((currentTime - startTime) / 60000);
    
    return elapsedTime;
  }, [currentWorkoutState]);

  const isWorkoutStarted = useCallback(() => {
    return !!currentWorkoutState;
  }, [currentWorkoutState]);

  const canCompleteWorkout = useCallback(() => {
    if (!currentWorkoutState || !userProgress) return false;
    
    const completedExercises = currentWorkoutState.exercises.filter(ex => ex.completed).length;
    const totalExercises = currentWorkoutState.exercises.length;
    
    return completedExercises === totalExercises;
  }, [currentWorkoutState]);

  return {
    userProgress,
    currentWorkoutState,
    isLoading,
    error,
    isOnline,
    initializeProgress,
    resetProgress,
    initializeCurrentWorkout,
    startWorkout,
    updateCurrentSet,
    completeCurrentExercise,
    isExerciseCompleted,
    finishCurrentWorkout,
    resetCurrentWorkout,
    progressToNextDay,
    skipCurrentDay,
    advanceToNextWeek,
    addRandomWeek1And2Stats,
    getCurrentWeekExercises,
    getWorkoutStats,
    getRecentWorkouts,
    getElapsedTime,
    isWorkoutStarted,
    canCompleteWorkout,
    getCacheStatus,
    debugWorkoutData,
    cleanupDuplicateSessions
  };
}; 